import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_SEO, ROUTE_SEO_MAP } from "../data/seoConfig";

/**
 * RouteSEO: Global Headless Component
 * Automatically detects current route and updates <head> metadata:
 * - document.title
 * - meta description, keywords, robots, author
 * - canonical link
 * - OpenGraph tags (og:title, og:description, og:image, og:url, og:type)
 * - Twitter Card tags (twitter:card, twitter:title, twitter:description, twitter:image)
 * - Dynamic Schema.org JSON-LD (Breadcrumbs & Page Schema)
 */
const RouteSEO = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";

    // 1. Find exact match or normalized alias
    let config = ROUTE_SEO_MAP[pathname];

    // 2. Fallback prefix matchers for nested routes
    if (!config) {
      if (pathname.startsWith("/careers/") || pathname.startsWith("/career/")) {
        config = {
          title: "Career Opportunity | Simpel — The Cognitive AI Company",
          description: "Explore career opportunities at Simpel. Build cutting-edge cognitive AI systems.",
          canonical: pathname,
        };
      } else if (pathname.startsWith("/case-studies/") || pathname.startsWith("/case-study/")) {
        config = {
          title: "Case Study | Proven Enterprise Impact | Simpel",
          description: "Discover real-world enterprise results and measurable ROI delivered by Simpel.",
          canonical: pathname,
        };
      } else if (pathname.startsWith("/cognitive/") || pathname.startsWith("/industries/")) {
        config = {
          title: "Cognitive AI Industry Copilot | Simpel",
          description: "Specialized cognitive AI copilots designed for deep domain enterprise operations.",
          canonical: pathname,
        };
      } else {
        config = {
          ...DEFAULT_SEO,
          canonical: pathname,
        };
      }
    }

    // Merge with defaults
    const activeConfig = { ...DEFAULT_SEO, ...config };

    // 3. Document Title
    const formattedTitle = activeConfig.title.includes("Simpel")
      ? activeConfig.title
      : `${activeConfig.title} | Simpel`;
    document.title = formattedTitle;

    // Helper: update meta tag
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", Array.isArray(val) ? val.join(", ") : val);
    };

    // Helper: update canonical
    const setCanonical = (href) => {
      if (!href) return;
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      const fullUrl = href.startsWith("http")
        ? href
        : `${window.location.origin}${href.startsWith("/") ? "" : "/"}${href}`;
      link.setAttribute("href", fullUrl);
    };

    // 4. Update Meta Tags
    setMeta("name", "description", activeConfig.description);
    setMeta("name", "keywords", activeConfig.keywords);
    setMeta("name", "robots", activeConfig.robots || DEFAULT_SEO.robots);

    // 5. Update OpenGraph Tags
    const fullCanonical = activeConfig.canonical?.startsWith("http")
      ? activeConfig.canonical
      : `${window.location.origin}${activeConfig.canonical?.startsWith("/") ? "" : "/"}${activeConfig.canonical || ""}`;

    const resolvedImage = activeConfig.ogImage
      ? activeConfig.ogImage.startsWith("http")
      ? activeConfig.ogImage
      : `${window.location.origin}${activeConfig.ogImage.startsWith("/") ? "" : "/"}${activeConfig.ogImage}`
      : `${window.location.origin}/images/simpel-icon-mark.png`;

    setMeta("property", "og:title", activeConfig.ogTitle || formattedTitle);
    setMeta("property", "og:description", activeConfig.ogDescription || activeConfig.description);
    setMeta("property", "og:type", activeConfig.ogType || "website");
    setMeta("property", "og:url", fullCanonical);
    setMeta("property", "og:image", resolvedImage);
    setMeta("property", "og:site_name", "Simpel");

    // 6. Update Twitter Card Tags
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", activeConfig.twitterTitle || formattedTitle);
    setMeta("name", "twitter:description", activeConfig.twitterDescription || activeConfig.description);
    setMeta("name", "twitter:image", resolvedImage);

    // 7. Canonical URL
    setCanonical(activeConfig.canonical || pathname);

    // 8. Dynamic Schema.org JSON-LD Generation
    const setJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Breadcrumb Schema
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      const breadcrumbList = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://simpel.ai/",
          },
          ...segments.map((seg, idx) => {
            const path = "/" + segments.slice(0, idx + 1).join("/");
            const name = seg
              .split("-")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ");
            return {
              "@type": "ListItem",
              "position": idx + 2,
              "name": name,
              "item": `https://simpel.ai${path}`,
            };
          }),
        ],
      };
      setJsonLd("route-breadcrumbs-schema", breadcrumbList);
    }

    // Page Specific Schema
    let pageSchema = null;
    if (pathname.includes("copilot") || pathname.includes("assistant") || pathname.includes("intelligence")) {
      pageSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": activeConfig.title.split("|")[0].trim(),
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Cloud-native, Web",
        "description": activeConfig.description,
        "offers": {
          "@type": "Offer",
          "price": "Contact for pricing",
          "priceCurrency": "USD",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Simpel",
          "url": "https://simpel.ai",
        },
      };
    } else if (pathname.startsWith("/case-studies/")) {
      pageSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": activeConfig.title.split("|")[0].trim(),
        "description": activeConfig.description,
        "image": resolvedImage,
        "publisher": {
          "@type": "Organization",
          "name": "Simpel",
          "url": "https://simpel.ai",
        },
      };
    } else {
      pageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": formattedTitle,
        "description": activeConfig.description,
        "url": fullCanonical,
      };
    }

    if (pageSchema) {
      setJsonLd("route-page-schema", pageSchema);
    }
  }, [location.pathname]);

  return null;
};

export default RouteSEO;
